import React, { useMemo } from 'react';
import { Stack, Divider, Tabs, Button, ScrollArea } from '@mantine/core';
import { IconMail, IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { MobileJournalCalendar } from './MobileJournalCalendar';
import { MobileJournalDayPanel } from './MobileJournalDayPanel';
import {
  IJournal,
  IJournalEntry,
} from '../../../../global/entities/Journal/Journal';
import tabClasses from '../../../HcpPatientDashboard/Graph/tab.module.css';

dayjs.extend(utc);

interface MobileJournalLayoutProps {
  journals: IJournal[];
  activeTab: string | null;
  onTabChange: (value: string | null) => void;
  entries: IJournalEntry[];
  calendarDays: Date[];
  currentMonth: Date;
  selectedDate: Date;
  onMonthChange: (date: Date) => void;
  onDateSelect: (date: Date) => void;
  onViewEntry: (entry: IJournalEntry) => void;
  onAddEntry: (dateString: string) => void;
  isHcpView: boolean;
  onSendReminder: () => void;
  maxDots?: number;
}

export const MobileJournalLayout: React.FC<MobileJournalLayoutProps> = ({
  journals,
  activeTab,
  onTabChange,
  entries,
  calendarDays,
  currentMonth,
  selectedDate,
  onMonthChange,
  onDateSelect,
  onViewEntry,
  onAddEntry,
  isHcpView,
  onSendReminder,
  maxDots,
}) => {
  const { t } = useTranslation();

  const entriesForSelectedDate = useMemo(() => {
    const targetDateString = dayjs(selectedDate).format('YYYY-MM-DD');
    return entries.filter((entry) => {
      const entryDateString = dayjs
        .utc(entry.entryDate)
        .local()
        .format('YYYY-MM-DD');
      return entryDateString === targetDateString;
    });
  }, [entries, selectedDate]);

  return (
    <Stack gap="sm" p="xs">
      {/* Journal Tabs - Scrollable */}
      <ScrollArea type="auto" scrollbarSize={4}>
        <Tabs value={activeTab} onChange={onTabChange} classNames={tabClasses}>
          <Tabs.List style={{ flexWrap: 'nowrap' }}>
            {journals.map((journal) => (
              <Tabs.Tab
                key={journal.id}
                value={journal.id.toString()}
                style={{ whiteSpace: 'nowrap' }}
              >
                {journal.name}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </ScrollArea>

      {/* Content */}
      <MobileJournalCalendar
        currentMonth={currentMonth}
        selectedDate={selectedDate}
        onDateSelect={onDateSelect}
        onMonthChange={onMonthChange}
        entries={entries}
        calendarDays={calendarDays}
        maxDots={maxDots}
      />

      {!isHcpView && (
        <Button
          variant="light"
          size="sm"
          fullWidth
          leftSection={<IconPlus size={14} />}
          onClick={() => onAddEntry(dayjs(selectedDate).format('YYYY-MM-DD'))}
        >
          {t('journal.addEntry', 'Add Entry')}
        </Button>
      )}

      <MobileJournalDayPanel
        selectedDate={selectedDate}
        entries={entriesForSelectedDate}
        onViewEntry={onViewEntry}
        onAddEntry={onAddEntry}
        isHcpView={isHcpView}
      />

      {/* HCP Actions */}
      {isHcpView && (
        <Button
          variant="outline"
          size="sm"
          fullWidth
          leftSection={<IconMail size={16} />}
          onClick={onSendReminder}
        >
          {t('journal.sendReminder', 'Send Reminder')}
        </Button>
      )}
    </Stack>
  );
};
